import { useTranslation } from 'react-i18next'
import { MultiFiltro } from '@/components/common/MultiFiltro'
import {
  BarraFiltrosActivos, descripcionDe, filtrosDe, type FiltroActivo,
} from '@/components/common/BarraFiltrosActivos'
import { etiquetaCatalogoDe } from '@/utils/filtroArticulo'
import type { InventarioSortKey } from '@/types'
import { useVariasEmpresas } from '@/hooks/useVariasEmpresas'
import {
  filterFieldCls,
  filterLabelCls,
  filterCardCls,
} from '@/components/common/FilterBar'

interface InventarioFiltersProps {
  dark: boolean
  articulo: string[]
  descripcion: string[]
  /** Buscador único de antes; sólo llega desde enlaces `?q=…` guardados. */
  terminos: string[]
  /** Catálogo de artículos de las filas cargadas, para sugerir al escribir. */
  opcionesArticulo?: { valor: string; titulo: string; detalle?: string }[]
  empresa: string
  almacen: string
  linea: string
  familia: string
  tipo: string
  soloStock: boolean
  sortKey: InventarioSortKey
  sortDir: 'asc' | 'desc'
  /** Feedback de resultados, ya formateado por la página. */
  resumen?: string
  allEmpresas: string[]
  allAlmacenes: string[]
  allLineas: string[]
  allFamilias: string[]
  isArmafLocked: boolean
  isEmpresaAll: boolean
  setArticulo: (v: string[]) => void
  setDescripcion: (v: string[]) => void
  setTerminos: (v: string[]) => void
  setEmpresa: (v: string) => void
  setAlmacen: (v: string) => void
  setLinea: (v: string) => void
  setFamilia: (v: string) => void
  setTipo: (v: string) => void
  setSoloStock: (v: boolean) => void
  setSortKey: (v: InventarioSortKey) => void
  setSortDir: (v: 'asc' | 'desc') => void
  clearAll: () => void
}

export function InventarioFilters({
  dark,
  articulo, descripcion, terminos, opcionesArticulo = [], empresa, almacen, linea, familia, tipo, soloStock, sortKey, sortDir,
  resumen, allEmpresas, allAlmacenes, allLineas, allFamilias, isArmafLocked, isEmpresaAll,
  setArticulo, setDescripcion, setTerminos, setEmpresa, setAlmacen, setLinea, setFamilia, setTipo, setSoloStock,
  setSortKey, setSortDir, clearAll,
}: InventarioFiltersProps) {
  const { t } = useTranslation()

  const SORT_OPTIONS: { key: InventarioSortKey; label: string }[] = [
    { key: 'articulo',            label: t('inventory.sort.article') },
    { key: 'descripcion',         label: t('inventory.sort.description') },
    { key: 'familia',             label: t('inventory.sort.family') },
    { key: 'almacen',             label: t('inventory.sort.warehouse') },
    { key: 'cantidadInventarioFisico', label: t('inventory.col.physicalStock') },
    { key: 'cantidadFisicaDisponible', label: t('inventory.col.physicalAvail') },
    { key: 'cantidadTotalDisponible',  label: t('inventory.col.totalAvailable') },
  ]

  const labelCls   = filterLabelCls(dark)
  const btnBase    = 'text-[11px] px-2.5 py-1.5 rounded-lg border transition-colors font-medium cursor-pointer'
  const btnNeutral = dark
    ? `${btnBase} border-white/10 bg-white/5 text-slate-300 hover:bg-white/10`
    : `${btnBase} border-slate-200 bg-white text-slate-500 hover:bg-slate-50 shadow-sm`
  const btnActive  = `${btnBase} bg-sky-500 border-sky-500 text-white`

  // Mas de UNA empresa que elegir, no el rol `Empresa.All`: con AVI+TDIS
  // habia dos empresas y ningun desplegable con el que separarlas.
  const showEmpresaFilter = useVariasEmpresas()

  // El chip enseña el mismo rótulo que la opción del desplegable —formateado—
  // mientras el valor que filtra sigue siendo el crudo. Lo que se tecleó a mano
  // y no está en el catálogo se deja intacto: no es un código de artículo.
  const filtrosActivos: FiltroActivo[] = [
    ...filtrosDe(articulo, setArticulo, {
      campo: t('inventory.col.article'),
      etiquetaDe: etiquetaCatalogoDe(opcionesArticulo),
      tooltipDe: descripcionDe(opcionesArticulo),
      prefijo: 'art:',
    }),
    ...filtrosDe(descripcion, setDescripcion, {
      campo: t('common.busqueda.descripcion'), prefijo: 'desc:',
    }),
    // Los del buscador antiguo no salen de ningún campo en concreto: se les
    // pone su propio nombre en vez de atribuirlos a uno.
    ...filtrosDe(terminos, setTerminos, {
      campo: t('common.busqueda.legado'),
      etiquetaDe: etiquetaCatalogoDe(opcionesArticulo),
      tooltipDe: descripcionDe(opcionesArticulo),
      prefijo: 'q:',
    }),
    ...(empresa ? [{ clave: 'emp', campo: t('common.company'), valor: empresa.toUpperCase(), quitar: () => setEmpresa('') }] : []),
    ...(almacen ? [{ clave: 'alm', campo: t('inventory.col.warehouse'), valor: almacen, quitar: () => setAlmacen('') }] : []),
    ...(linea ? [{ clave: 'lin', campo: t('inventory.filter.line'), valor: linea, quitar: () => setLinea('') }] : []),
    ...(familia ? [{ clave: 'fam', campo: t('common.family'), valor: familia, quitar: () => setFamilia('') }] : []),
  ]

  return (
    <div className={filterCardCls(dark)}>
    <div className="p-3 flex flex-wrap gap-3 items-end">

      {/* DOS buscadores donde antes había uno. El de antes metía código,
          descripción, línea, familia y almacén en el mismo texto, y ahí dentro
          dos términos sólo pueden combinarse con OR. Línea, familia y almacén
          no se convierten en buscadores: ya tienen su desplegable al lado. */}
      <div className="flex flex-col gap-1 min-w-[170px] flex-1">
        <label className={labelCls}>{t('inventory.col.article')}</label>
        {/* Catálogo de artículos Y texto libre: las sugerencias aceleran lo más
            frecuente sin cerrar la puerta a pegar un código que no esté. */}
        <MultiFiltro
          dark={dark}
          permitirLibre
          opciones={opcionesArticulo}
          seleccion={articulo}
          onSeleccion={setArticulo}
          placeholder={t('common.search')}
          title={t('common.busqueda.pistaPegar')}
        />
      </div>

      <div className="flex flex-col gap-1 min-w-[170px] flex-1">
        <label className={labelCls}>{t('common.busqueda.descripcion')}</label>
        <MultiFiltro
          dark={dark}
          permitirLibre
          seleccion={descripcion}
          onSeleccion={setDescripcion}
          placeholder={t('common.search')}
          title={t('common.busqueda.pistaPegar')}
        />
      </div>

      {/* Empresa */}
      {showEmpresaFilter && (
        <div className="flex flex-col gap-1 min-w-[110px]">
          <label className={labelCls}>{t('common.company')}</label>
          <select
            value={empresa}
            onChange={e => setEmpresa(e.target.value)}
            className={`${filterFieldCls(dark, !!empresa)} cursor-pointer`}
          >
            <option value="">{t('common.allCompanies')}</option>
            {allEmpresas.map(e => <option key={e} value={e}>{e.toUpperCase()}</option>)}
          </select>
        </div>
      )}

      {/* Almacén — solo visible para usuarios con Empresa.All */}
      {isEmpresaAll && (
        <div className="flex flex-col gap-1 min-w-[140px]">
          <label className={labelCls}>{t('inventory.filter.warehouse')}</label>
          <select
            value={almacen}
            onChange={e => setAlmacen(e.target.value)}
            className={`${filterFieldCls(dark, !!almacen)} cursor-pointer`}
          >
            <option value="">{t('inventory.filter.allWarehouses')}</option>
            {allAlmacenes.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      )}

      {/* Línea */}
      <div className="flex flex-col gap-1 min-w-[140px]">
        <label className={labelCls}>
          {t('inventory.filter.line')}
          {isArmafLocked && (
            <span className={`ml-1.5 text-[9px] font-bold px-1 py-px rounded ${
              dark ? 'bg-amber-900/40 text-amber-400' : 'bg-amber-50 text-amber-600 border border-amber-200'
            }`}>
              {t('common.locked')}
            </span>
          )}
        </label>
        <select
          value={isArmafLocked ? 'ARMAF' : linea}
          onChange={e => setLinea(e.target.value)}
          disabled={isArmafLocked}
          className={`${filterFieldCls(dark, !!linea && !isArmafLocked)} cursor-pointer ${isArmafLocked ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          {isArmafLocked ? (
            <option value="ARMAF">ARMAF</option>
          ) : (
            <>
              <option value="">{t('inventory.filter.allLines')}</option>
              {allLineas.map(l => <option key={l} value={l}>{l}</option>)}
            </>
          )}
        </select>
      </div>

      {/* Familia — visible para todos los roles (incl. AVP) */}
      <div className="flex flex-col gap-1 min-w-[140px]">
        <label className={labelCls}>{t('common.family')}</label>
        <select
          value={familia}
          onChange={e => setFamilia(e.target.value)}
          className={`${filterFieldCls(dark, !!familia)} cursor-pointer`}
        >
          <option value="">{t('common.allFem')}</option>
          {allFamilias.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
      </div>

      {/* Tipo de material — 'P' = producto final; el resto = componente */}
      <div className="flex flex-col gap-1 min-w-[150px]">
        <label className={labelCls}>{t('inventory.filter.materialType')}</label>
        <select
          value={tipo}
          onChange={e => setTipo(e.target.value)}
          className={`${filterFieldCls(dark, !!tipo)} cursor-pointer`}
        >
          <option value="">{t('inventory.filter.allTypes')}</option>
          <option value="final">{t('inventory.filter.finishedProduct')}</option>
          <option value="componente">{t('inventory.filter.component')}</option>
        </select>
      </div>

      {/* Ordenar */}
      <div className="flex flex-col gap-1 min-w-[160px]">
        <label className={labelCls}>{t('common.sortBy')}</label>
        <div className="flex gap-1">
          <select
            value={sortKey}
            onChange={e => setSortKey(e.target.value as InventarioSortKey)}
            className={`${filterFieldCls(dark)} flex-1 cursor-pointer`}
          >
            {SORT_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
          <button
            onClick={() => setSortDir(sortDir === 'asc' ? 'desc' : 'asc')}
            className={`${btnNeutral} !px-2 shrink-0 flex items-center justify-center`}
            title={sortDir === 'asc' ? t('common.asc') : t('common.desc')}
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {sortDir === 'asc'
                ? <path d="M3 9l4-4 4 4" />
                : <path d="M3 5l4 4 4-4" />
              }
            </svg>
          </button>
        </div>
      </div>

      {/* Toggles */}
      <div className="flex items-end gap-2 flex-wrap">
        <button
          onClick={() => setSoloStock(!soloStock)}
          className={soloStock ? btnActive : btnNeutral}
        >
          {t('inventory.filter.onlyWithStock')}
        </button>

      </div>
    </div>

    {/* Franja común, separada de los controles: éstos no se mueven nunca. */}
    {filtrosActivos.length > 0 && (
      <div className="px-3 pb-3 -mt-1">
        <BarraFiltrosActivos
          dark={dark}
          filtros={filtrosActivos}
          onLimpiar={clearAll}
          resumen={resumen}
        />
      </div>
    )}
    </div>
  )
}
